import { Controller } from "@hotwired/stimulus";

// アカウント情報（メール / パスワード / パスワード確認）系フォームのリアルタイムバリデーション。
// Customer の PASSWORD_FORMAT および length: { within: 8..128 } に合わせた判定。
//
// 利用箇所:
//   - /registration (新規登録: email + password + password_confirmation)
//   - /mypage/password/edit (パスワード変更: password + password_confirmation のみ)
//
// TODO: LP (public/lp/radineer-2510/assets/js/form-validation.js) と同じ
// バリデーションロジックを別実装で持っている。LP を Vite エントリポイント化したタイミングで
// 共通モジュール (utils/account-form-validators.js 等) に切り出して両方から参照する形にしたい。

// メール形式チェック用の正規表現（LP 側と同じ。TLD 必須）
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;

// パスワードの各条件に応じたエラーメッセージ（LP 側と同じ文言）
const PASSWORD_ERROR_MESSAGES = {
  lengthMin: "8文字以上で入力してください",
  lengthMax: "128文字以内で入力してください",
  uppercase: "英大文字を含めてください",
  lowercase: "英小文字を含めてください",
  digit: "数字を含めてください",
};
const EMAIL_FORMAT_INVALID = "メールアドレスの形式が正しくありません";
const PASSWORD_CONFIRM_MISMATCH = "パスワードが一致しません";

export class AccountFormValidationController extends Controller {
  // バリデーション対象の input / チェック表示 / エラー表示要素の target 名
  static targets = [
    "emailInput",
    "emailCheck",
    "emailError",
    "passwordInput",
    "passwordCheck",
    "passwordError",
    "passwordConfirmationInput",
    "passwordConfirmationCheck",
    "passwordConfirmationError",
  ];

  // ============================================================
  // Lifecycle
  // ============================================================

  // 初期表示：パスワード条件リストを未達状態（チェックマーク非表示）にする
  connect() {
    this.updatePasswordConditions();
  }

  // ============================================================
  // Email
  // ============================================================

  // メール欄の形式をチェックし、チェック表示 or エラー表示を切り替える
  validateEmail() {
    const value = this.emailInputTarget.value.trim();
    this.setNeutral(this.emailCheckTarget, this.emailErrorTarget);

    if (value === "") return;
    if (EMAIL_REGEX.test(value)) {
      this.setValid(this.emailCheckTarget, this.emailErrorTarget);
    } else {
      this.setInvalid(this.emailCheckTarget, this.emailErrorTarget, EMAIL_FORMAT_INVALID);
    }
  }

  // ============================================================
  // Password
  // ============================================================

  // パスワード欄の input イベント時の複合処理。
  // パスワード本体のバリデーション + 条件リスト更新 + 確認欄との一致再判定をまとめて行う。
  onPasswordInput() {
    this.validatePassword();
    this.updatePasswordConditions();
    if (this.hasPasswordConfirmationInputTarget) this.validatePasswordConfirmation();
  }

  // パスワード欄の条件をチェックし、チェック表示 or エラー表示を切り替える
  validatePassword() {
    const value = this.passwordInputTarget.value;
    this.setNeutral(this.passwordCheckTarget, this.passwordErrorTarget);
    if (value === "") return;
    const message = this.passwordErrorMessage(value);
    if (!message) {
      this.setValid(this.passwordCheckTarget, this.passwordErrorTarget);
    } else {
      this.setInvalid(this.passwordCheckTarget, this.passwordErrorTarget, message);
    }
  }

  // パスワード文字列が条件を満たすか判定し、最初に引っかかったメッセージを返す
  // （LP 側 form-validation.js の判定順序とメッセージ文言に揃える）
  passwordErrorMessage(value) {
    if (value.length < PASSWORD_MIN_LENGTH) return PASSWORD_ERROR_MESSAGES.lengthMin;
    if (value.length > PASSWORD_MAX_LENGTH) return PASSWORD_ERROR_MESSAGES.lengthMax;
    if (!/[A-Z]/.test(value)) return PASSWORD_ERROR_MESSAGES.uppercase;
    if (!/[a-z]/.test(value)) return PASSWORD_ERROR_MESSAGES.lowercase;
    if (!/[0-9]/.test(value)) return PASSWORD_ERROR_MESSAGES.digit;
    return null;
  }

  // パスワード入力に応じて password_description partial の各条件 li に
  // is-valid クラスを付けて、チェックマーク表示を動的に更新する。
  // 該当する [data-condition] 要素が無い画面では何もしない（partial 非表示時）。
  updatePasswordConditions() {
    const value = this.passwordInputTarget.value;
    const items = this.element.querySelectorAll("[data-condition]");
    items.forEach((item) => {
      const type = item.dataset.condition;
      let isValid = false;
      switch (type) {
        case "length":
          isValid = value.length >= PASSWORD_MIN_LENGTH;
          break;
        case "uppercase":
          isValid = /[A-Z]/.test(value);
          break;
        case "lowercase":
          isValid = /[a-z]/.test(value);
          break;
        case "number":
          isValid = /[0-9]/.test(value);
          break;
      }
      item.classList.toggle("is-valid", isValid);
    });
  }

  // ============================================================
  // Password Confirmation
  // ============================================================

  // 確認欄がパスワード欄と一致するかチェックする
  validatePasswordConfirmation() {
    const value = this.passwordConfirmationInputTarget.value;
    const password = this.passwordInputTarget.value;
    this.setNeutral(this.passwordConfirmationCheckTarget, this.passwordConfirmationErrorTarget);

    if (value === "") return;

    if (value !== password) {
      this.setInvalid(
        this.passwordConfirmationCheckTarget,
        this.passwordConfirmationErrorTarget,
        PASSWORD_CONFIRM_MISMATCH,
      );
    } else {
      this.setValid(this.passwordConfirmationCheckTarget, this.passwordConfirmationErrorTarget);
    }
  }

  // ============================================================
  // UI state helpers (全 action から呼ばれる共通ヘルパー)
  // ============================================================

  // 未入力状態：チェック非表示・エラー非表示にする
  setNeutral(checkEl, errorEl) {
    if (checkEl) {
      checkEl.classList.add("is-neutral");
      checkEl.classList.remove("is-valid");
    }
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.classList.add("is-hidden");
    }
  }

  // 入力OK状態：緑チェック表示・エラー非表示にする
  setValid(checkEl, errorEl) {
    if (checkEl) {
      checkEl.classList.remove("is-neutral");
      checkEl.classList.add("is-valid");
    }
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.classList.add("is-hidden");
    }
  }

  // 入力NG状態：チェック非表示・エラーメッセージを表示する
  setInvalid(checkEl, errorEl, message) {
    if (checkEl) {
      checkEl.classList.remove("is-valid");
      checkEl.classList.add("is-neutral");
    }
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove("is-hidden");
    }
  }
}
